import React, { useEffect, useRef, useState } from 'react';
import { Col, ControlLabel, FormGroup, HelpBlock, Row } from 'react-bootstrap';
import { Field } from 'react-final-form';
import { getOrgTermsContent } from '../../../content/utils.js';
import { FormError } from '../../FormError.jsx';
import { UploadIcon } from '../../icons/UploadIcon.jsx';
import MultistepFormsFooter from '../../MultistepFormsFooter.jsx';
import MultistepFormsNav from '../../headers/FormHeaders/MultistepFormsNav.jsx';
import FieldGroup from '../../FieldGroup.jsx';
import MultiSelect from '../../MultiSelect.jsx';
import DatePickerAdapter from '../../DatePickerAdapter.jsx';
import RadioOptionsField from '../../RadioOptionsField.jsx';
import AuthorizedAction from '../../AuthorizedAction.js';
import EmployeesFormField from '../../employees-form-field/EmployeesFormField.jsx';

function StepGeneralForm(props) {
  const {
    editing,
    handleSubmit,
    submitFailed,
    submitError,
    onCancel,
    owners,
    projects,
    parentPortfolios,
    projectStatuses,
    selectedEntities,
    updateSelectedEntities,
    onSelectSponsor,
    handleImageSelect,
    clearImageField,
    employeeSearchParams,
    onShowEmployeeModal,
    errors,
    loading,
    values,
  } = props;

  const [fileName, setFileName] = useState('');
  const inputRef = useRef();
  const file = inputRef.current ? inputRef.current.files[0] : null;

  const statusOptions = projectStatuses.map((s) => ({
    ...s,
    label: s.archived ? `${s.name} (archived)` : s.name,
    value: s.name,
  }));

  useEffect(() => {
    if (file) {
      setFileName(file.name);
    }
  }, [file]);

  return (
    <form onSubmit={handleSubmit}>
      <Row>
        <Col xs={12}>
          <MultistepFormsNav
            type="portfolio"
            activeIndex={0}
            onSubmit={handleSubmit}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <Field
            id="name"
            name="name"
            label={getOrgTermsContent('portfolioName')}
            placeholder="Start Typing"
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <ControlLabel>
            Select {getOrgTermsContent('parentPortfolio')}
          </ControlLabel>
          <MultiSelect
            multiple={false}
            options={parentPortfolios || []}
            selectedOptions={selectedEntities.parentPortfolio}
            updateSelectedOptions={(val) =>
              updateSelectedEntities('parentPortfolio', val)
            }
            placeholder={`Select ${getOrgTermsContent('parentPortfolio')}`}
            deleteMsg={`Are you sure you want to detach this ${getOrgTermsContent('parentPortfolio')}?`}
            subject="Parent Portfolio"
            nameKey="name"
            additionalFields={{}}
            error={null}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <ControlLabel>Select Owner(s)</ControlLabel>
          <MultiSelect
            options={owners || []}
            selectedOptions={selectedEntities.owners}
            updateSelectedOptions={(val) =>
              updateSelectedEntities('owners', val)
            }
            placeholder="Select People"
            subject="portfolio owner"
            nameKey="fullName"
            additionalFields={{}}
            deleteMsg={`Are you sure you want to detach this ${getOrgTermsContent('portfolio')} owner?`}
            error={null}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8} className="m-b-24">
          <EmployeesFormField
            selected={selectedEntities.sponsors}
            handleSelect={onSelectSponsor}
            additionalSearchParams={employeeSearchParams}
            title="Select Sponsor(s)"
            isItemActive={(val) =>
              selectedEntities.sponsors.some(({ id }) => id === val)
            }
            addBtn={
              <AuthorizedAction action="create" subject="employee">
                <button
                  type="button"
                  className="btn-link"
                  onClick={onShowEmployeeModal}
                >
                  {' '}
                  + Add People
                </button>
              </AuthorizedAction>
            }
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <ControlLabel>Select {getOrgTermsContent('project')}(s)</ControlLabel>
          <MultiSelect
            options={projects}
            selectedOptions={selectedEntities.projects}
            updateSelectedOptions={(val) =>
              updateSelectedEntities('projects', val)
            }
            placeholder={`Select ${getOrgTermsContent('project')}(s)`}
            deleteMsg={`Are you sure you want to delete this ${getOrgTermsContent('project')}?`}
            subject="Project"
            nameKey="name"
            additionalFields={{}}
            error={null}
          />
        </Col>
      </Row>

      <Row>
        <Col sm={8}>
          <Row>
            <Col sm={6}>
              <Field
                id="startDate"
                name="start_date"
                label="Start Date"
                placeholderText="Select a date"
                component={DatePickerAdapter}
                allowNull
                defaultValue={null}
              />
            </Col>
            <Col sm={6}>
              <Field
                id="endDate"
                name="end_date"
                label="End Date"
                placeholderText="Select a date"
                component={DatePickerAdapter}
                allowNull
                defaultValue={null}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col sm={8}>
          <RadioOptionsField
            id="status"
            label="Status"
            name="project_status"
            options={
              editing
                ? statusOptions
                : statusOptions.filter((item) => !item.archived)
            }
          />
        </Col>
      </Row>

      <Row>
        <Col xs={5}>
          <FormGroup className="flex flex-col flex-align-start">
            <ControlLabel>Upload An Image</ControlLabel>
            <div className="m-b-8 w-100">
              <label htmlFor="avatar" className="file-input">
                <UploadIcon />
                <span className="m-l-4">Upload Image</span>
                <input
                  style={{ display: 'none' }}
                  ref={inputRef}
                  id="avatar"
                  type="file"
                  accept=".jpeg, .png, .jpg"
                  onChange={handleImageSelect}
                />
              </label>
              <div className="file-name m-t-8">{fileName}</div>
            </div>
            {submitFailed && errors.image && (
              <FormGroup validationState="error m-0">
                <HelpBlock>{errors.image}</HelpBlock>
              </FormGroup>
            )}
            <p className="m-0 text-muted">Allowed formats: .jpeg, .png, .jpg</p>
            <p className="text-muted">Max file size: 10Mb</p>
          </FormGroup>
        </Col>
        {fileName ? (
          <Col xs={1}>
            <button
              type="button"
              className="btn-delete btn btn-default m-t-28"
              onClick={() => {
                clearImageField();
                setFileName('');
              }}
            ></button>
          </Col>
        ) : null}
      </Row>

      <Row>
        <Col xs={12}>
          {submitFailed && submitError ? (
            <FormError submitError={submitError} />
          ) : null}
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <MultistepFormsFooter
            values={values}
            loading={loading}
            handleSubmit={handleSubmit}
            onCancel={onCancel}
            disabledSaveAndExit={loading || !editing}
          />
        </Col>
      </Row>
    </form>
  );
}

export default StepGeneralForm;
